import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAppSelector } from '../../hooks/useAppDispatch'
import { api } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { ADMIN_USERS_PAGE_SIZE, ADMIN_SEARCH_DEBOUNCE_MS, COLOR_CYAN, COLOR_ACCENT_ROLE_BG, COLOR_CYAN_ROLE_BG } from '../../lib/config'
import { SearchInput, SentinelLoader } from '../../components/ui'
import Avatar from '../../components/ui/Avatar'
import UserHoverCard from '../../components/ui/UserHoverCard'
import { timeAgoShort } from '../../lib/avatar'
import { AdminRefreshBtn } from './AdminRefreshBtn'
import s from '../../styles/pages/AdminPage.module.css'

const MANAGEABLE_ROLES = ['admin', 'developer'] as const
type ManageableRole = typeof MANAGEABLE_ROLES[number]

const ROLE_STYLES: Record<string, { bg: string; color: string }> = {
  user:      { bg: 'var(--bg-elevated)',  color: 'var(--text-muted)' },
  admin:     { bg: COLOR_ACCENT_ROLE_BG, color: 'var(--accent-primary)' },
  developer: { bg: COLOR_CYAN_ROLE_BG,   color: COLOR_CYAN },
}

export function AdminUsers() {
  const { accessToken, user: me } = useAppSelector((s) => s.auth)
  const navigate = useNavigate()
  const [search, setSearch] = useState('')
  const [users, setUsers] = useState<any[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [nextCursor, setNextCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [roleLoading, setRoleLoading] = useState<string | null>(null)
  const [expandedUser, setExpandedUser] = useState<string | null>(null)
  const sentinelRef = useRef<HTMLDivElement>(null)

  const fetchUsers = useCallback(async (reset = false, cur?: string) => {
    if (reset) setLoading(true); else setLoadingMore(true)
    try {
      const params = new URLSearchParams({ limit: String(ADMIN_USERS_PAGE_SIZE) })
      if (search.trim()) params.set('search', search.trim())
      if (cur) params.set('cursor', cur)
      const d = await api.get<{ data: any[]; hasMore: boolean; nextCursor: string | null }>('users', `${ENDPOINTS.users.adminList}?${params}`, accessToken)
      setUsers(reset ? d.data : (prev) => [...prev, ...d.data])
      setHasMore(d.hasMore)
      setNextCursor(d.nextCursor)
    } catch {}
    if (reset) setLoading(false); else setLoadingMore(false)
  }, [search, accessToken])

  useEffect(() => {
    const t = setTimeout(() => void fetchUsers(true), ADMIN_SEARCH_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [search])

  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loadingMore) return
    const obs = new IntersectionObserver((entries) => {
      if (entries[0]?.isIntersecting && hasMore && !loadingMore) {
        void fetchUsers(false, nextCursor ?? undefined)
      }
    }, { rootMargin: '0px 0px 120px 0px' })
    obs.observe(sentinelRef.current)
    return () => obs.disconnect()
  }, [hasMore, loadingMore, nextCursor, fetchUsers])

  const handleToggleRole = async (userId: string, role: ManageableRole, hasRole: boolean) => {
    setRoleLoading(userId)
    try {
      const endpoint = hasRole ? ENDPOINTS.users.removeRole(userId) : ENDPOINTS.users.addRole(userId)
      await api.patch('users', endpoint, { role }, accessToken)
      setUsers((prev) => prev.map((u) =>
        u.id !== userId ? u : { ...u, roles: hasRole ? u.roles.filter((r: string) => r !== role) : [...u.roles, role] }
      ))
    } catch (err: any) { alert(`Failed to update role: ${err?.message ?? 'Unknown error'}`) }
    setRoleLoading(null)
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', marginBottom: '1rem' }}>
        <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name or email..." containerStyle={{ flex: 1 }} />
        <AdminRefreshBtn onClick={() => void fetchUsers(true)} loading={loading} />
      </div>

      <div className={s.listCard}>
        {loading && users.length === 0 && <div className={s.listCardLoading}>Loading...</div>}
        {!loading && users.length === 0 && <div className={s.listCardEmpty}>No users found</div>}
        {users.map((u, i) => {
          const displayName = u.profile?.displayName ?? u.email ?? 'User'
          return (
            <div key={u.id} style={{ borderBottom: i < users.length - 1 ? '1px solid var(--border)' : 'none' }}>
              <div className={s.userRow} onClick={() => void navigate(`/profile/${u.id}`)}>
                <UserHoverCard userId={u.id} currentUserId={me?.id} displayName={displayName} avatarUrl={u.profile?.avatarUrl}>
                  <div className={s.userIdentity}>
                    <Avatar name={displayName} avatarUrl={u.profile?.avatarUrl} size={34} />
                    <div className={s.userInfo}>
                      <div className={s.userName}>{displayName}</div>
                      <div className={s.userEmail}>{u.email}</div>
                    </div>
                  </div>
                </UserHoverCard>
                <div className={s.userRoles}>
                  {u.roles.map((r: string) => (
                    <span key={r} className={s.userRoleBadge} style={{ background: ROLE_STYLES[r]?.bg ?? 'var(--bg-elevated)', color: ROLE_STYLES[r]?.color ?? 'var(--text-muted)' }}>{r}</span>
                  ))}
                </div>
                <div className={s.userTime}>{timeAgoShort(u.createdAt)}</div>
                <span className={s.userChevron}>›</span>
                <button
                  onClick={e => { e.stopPropagation(); setExpandedUser(expandedUser === u.id ? null : u.id) }}
                  className={`${s.userManageBtn} ${expandedUser === u.id ? s.active : ''}`}
                >
                  {expandedUser === u.id ? 'Done' : 'Manage'}
                </button>
              </div>
              {expandedUser === u.id && (
                <div className={s.userExpanded}>
                  <div className={s.userRolesRow}>
                    <span className={s.userRolesLabel}>Roles:</span>
                    {MANAGEABLE_ROLES.map((role) => {
                      const hasRole = u.roles.includes(role)
                      const isUpdating = roleLoading === u.id
                      return (
                        <button key={role} onClick={() => void handleToggleRole(u.id, role, hasRole)} disabled={isUpdating}
                          className={s.userRoleToggle}
                          style={{ background: hasRole ? (ROLE_STYLES[role]?.bg ?? 'transparent') : 'transparent', color: hasRole ? (ROLE_STYLES[role]?.color ?? 'var(--text-muted)') : 'var(--text-muted)', border: `1px solid ${hasRole ? (ROLE_STYLES[role]?.color ?? 'var(--border)') : 'var(--border)'}`, cursor: isUpdating ? 'not-allowed' : 'pointer', fontWeight: hasRole ? 600 : 400, opacity: isUpdating ? 0.6 : 1 }}>
                          <span className={s.userRoleDot} style={{ background: hasRole ? (ROLE_STYLES[role]?.color ?? 'var(--text-muted)') : 'var(--border)' }} />
                          {role}
                          <span style={{ fontSize: '0.7rem', opacity: 0.7 }}>{hasRole ? '✓' : '+'}</span>
                        </button>
                      )
                    })}
                    <span className={s.userExpandedHint}>{roleLoading === u.id ? 'Updating...' : 'Click to toggle'}</span>
                  </div>
                  <div className={s.userExpandedClose}>
                    <button onClick={() => setExpandedUser(null)} style={{ padding: '0.3rem 0.75rem', borderRadius: '6px', fontSize: '0.75rem', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-muted)', cursor: 'pointer' }}>Close</button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      <SentinelLoader ref={sentinelRef} isFetching={loadingMore} />
    </div>
  )
}
